/**
 * Store promotion — WEB ONLY.
 *
 * abcshooter.web.app serves the complete game for free, while the iOS and Android builds are
 * paid. Without a pointer to the stores the free web build is a dead end: a family can play the
 * whole journey in a browser and never learn there is an app to buy. So on the web — and only
 * there — we show the two official store badges under the menu, and once a player has invested
 * enough to have earned `BUY_PROMPT_MIN_STARS` we follow every game with a dismissible buy card.
 *
 * Nothing in here ever renders inside the paid apps: `isWeb()` gates every entry point, and the
 * apps ARE the purchase. The paid builds get the rating prompt instead (see src/game/rating.ts).
 */
import i18next from 'i18next';
import { platform } from '@/platform';
import { devFlag } from '@/game/dev';
import { isClipMode } from '@/game/clip';

/** Storefront-neutral links — each sends the visitor to their own country's store. */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6790508038';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.abcshooter.twa';

/**
 * UTMs carried from the landing page into Play's `referrer`, in this order.
 *
 * Play logs the referrer server-side and reports it under **Play Console → Acquisition reports →
 * traffic source**, which is the only way left to learn whether an ad campaign sold anything:
 * Google Ads refuses to create Play conversions for a child-directed app at all
 * (`marketing/google-ads/README.md` §8.2), so there is no biddable purchase and no feedback loop —
 * just this, arriving a few days late, to divide into spend by hand.
 *
 * **Nothing is read inside the app.** Recovering the referrer *in* the app would mean the Install
 * Referrer API, which is exactly the tracking specs/analytics.md forbids and which Play Families
 * policy bars anyway. A longer store URL is all the Console report needs, so no binary changes and
 * every store declaration stays true.
 */
const REFERRER_UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * What an install gets credited to when a web visitor arrives at the store with no campaign of
 * their own — somebody who just found `abcshooter.web.app` and tapped a badge.
 *
 * Play supplies `utm_source=google-play&utm_medium=organic` for a genuine store visit, so without a
 * label of ours those two populations are indistinguishable in the Console. Naming ours is what
 * makes the free web build's central claim — that it is a shop window rather than a dead end
 * (specs/store-promo.md §1) — an answerable question instead of an argument.
 */
const WEB_REFERRER_SOURCE = 'abcsmash-web';
const WEB_REFERRER_MEDIUM = 'referral';

/**
 * `utm_medium` for the desktop QR route (src/game/qrPromo.ts, public/get.html).
 *
 * Its own medium because it is its own funnel and the interesting one: the visitor was on a laptop
 * that cannot install a phone app, and the install happened on a different device minutes later.
 * That handoff is the least attributable path to the store there is, and whether it survives at all
 * is exactly what the QR card was built to find out — which a row labelled `referral`, shared with
 * every ordinary badge tap, could never answer.
 */
export const QR_REFERRER_MEDIUM = 'qr';

/** Play truncates a referrer past 512 characters, so an overlong one is dropped rather than cut. */
const REFERRER_MAX = 512;

/**
 * The Play link to send *this* visitor to, carrying their campaign into Play's acquisition report.
 *
 * Returns the bare `PLAY_STORE_URL` in a paid app. The apps are the purchase, so there is no
 * acquisition to attribute, and `rating.ts` — the only in-app caller — deliberately keeps using the
 * constant: a rating request is not a campaign.
 */
export function playStoreUrl(search?: string, medium?: string): string {
  if (!isWeb()) return PLAY_STORE_URL;
  const s = search ?? (typeof location === 'undefined' ? '' : location.search);

  const from = new URLSearchParams(s);
  const vals: Partial<Record<(typeof REFERRER_UTMS)[number], string>> = {};
  for (const k of REFERRER_UTMS) {
    const v = from.get(k);
    if (v) vals[k] = v;
  }

  // No campaign of their own: somebody who simply found the site. Labelled rather than left blank,
  // because Play files a real store visit as utm_source=google-play and the two would be one row.
  if (Object.keys(vals).length === 0) {
    vals.utm_source = WEB_REFERRER_SOURCE;
    vals.utm_medium = WEB_REFERRER_MEDIUM;
  }
  // The route to the store, which the page's own UTMs cannot know — a QR hands off to another
  // device. Overrides the medium and keeps the campaign, so an ad that led to a scanned code is
  // still credited to that ad. Applied after the defaults so it wins over both.
  if (medium) vals.utm_medium = medium;

  // Re-emitted in REFERRER_UTMS order, never in the order they arrived: Play groups acquisition
  // rows by the literal referrer string, so ?utm_campaign=c&utm_source=s and ?utm_source=s&
  // utm_campaign=c must not become two rows for one campaign.
  const carried = new URLSearchParams();
  for (const k of REFERRER_UTMS) {
    const v = vals[k];
    if (v) carried.set(k, v);
  }

  const encoded = encodeURIComponent(carried.toString());
  if (encoded.length > REFERRER_MAX) return PLAY_STORE_URL;
  return `${PLAY_STORE_URL}&referrer=${encoded}`;
}

/**
 * Journey stars (0..25) the player must have reached before the buy card starts appearing.
 * Below this they are still trying the game out and a sales pitch would just be noise; at four
 * stars they have mastered ~40 words across several sessions and the app has earned the ask.
 */
export const BUY_PROMPT_MIN_STARS = 4;

/**
 * High-water mark of stars ever reached, across ALL lessons and languages. Journey progress is
 * per lesson, so the active lesson's star count drops back to 0 the moment a player opens a new
 * lesson — this monotonic mark is what the prompt gates on, so switching lessons never un-earns
 * the ask. Recorded wherever a stats() snapshot is already at hand (menu + round summary).
 */
const BEST_STARS_KEY = 'abcs.promo.bestStars.v1';

export function isWeb(): boolean {
  return platform().platform === 'web';
}

export function bestStarsReached(): number {
  try {
    const n = Number(localStorage.getItem(BEST_STARS_KEY));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;   // storage blocked (private mode): treat as a fresh player
  }
}

/**
 * The same high-water mark, kept PER LESSON (`{ packKey: stars }`). The free web allowance is counted
 * here, not on the global mark above: eight stars is what ONE lesson gives away, so a fresh lesson —
 * and the seasonal event lesson — starts with its own eight. The buy card keeps the global mark,
 * because "this player has invested in the game" is a statement about the player, not the lesson.
 */
const LESSON_STARS_KEY = 'abcs.promo.lessonStars.v1';

function readLessonStars(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(LESSON_STARS_KEY) ?? '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, number> : {};
  } catch {
    return {};   // storage blocked or corrupt: a fresh player
  }
}

/** Best stars ever reached in one lesson (`packKey`), 0 if it was never played. */
export function lessonStarsReached(lessonKey: string): number {
  const n = Number(readLessonStars()[lessonKey]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Raise the high-water marks. Never lowers them — star counts regress when a lesson is switched.
 * With a `lessonKey` the lesson's own mark is raised too (the free web allowance reads that one).
 */
export function recordStars(stars: number, lessonKey?: string): void {
  if (!Number.isFinite(stars)) return;
  const n = Math.floor(stars);
  if (lessonKey && n > lessonStarsReached(lessonKey)) {
    try {
      localStorage.setItem(LESSON_STARS_KEY, JSON.stringify({ ...readLessonStars(), [lessonKey]: n }));
    } catch { /* storage blocked — the allowance simply never trips */ }
  }
  if (n <= bestStarsReached()) return;
  try {
    localStorage.setItem(BEST_STARS_KEY, String(n));
  } catch { /* storage blocked — the prompt simply stays off */ }
}

/**
 * Web + enough invested progress. Checked before every buy card.
 *
 * `?buy` bypasses the star threshold so the card can be reviewed without first earning four
 * stars. It is a dev affordance in the same family as `?planet` and `?survival`: devFlag() is
 * inert on production hosts (see src/game/dev.ts), so it can never lower the bar for a real
 * player. It does NOT bypass the web check — the paid apps must never show a buy card.
 *
 * The clip/demo flow is excluded even though `?demo` also reads as 'web': it has its own
 * end-of-trial card (src/game/ui/clipOutro.ts) carrying the same store links, and two competing
 * "buy this" cards on one screen is one too many.
 */
export function shouldShowBuyPrompt(): boolean {
  if (!isWeb() || isClipMode()) return false;
  return devFlag('buy') || bestStarsReached() >= BUY_PROMPT_MIN_STARS;
}

/**
 * Journey stars the free web build gives away PER LESSON before that lesson stops (decided
 * 2026-10-01; counted per lesson, not across all of them, from 2026-10-02).
 *
 * Counted on the lesson's own monotonic high-water mark (`lessonStarsReached`), so a slip that drops
 * a star does not reopen the lesson, while a different lesson — whose bar starts at 0 — has its own
 * eight. Eight stars is ~80 mastered words on a 250-word lesson: long enough to see the method work,
 * short of the journey.
 */
export const WEB_FREE_STARS = 8;

/**
 * The free web build has run out FOR THIS LESSON (`lessonKey` = packKey of the active lesson): every
 * game start leads to the "continue in the app" card (src/game/ui/webLimitCard.ts) instead of a game.
 *
 * Web only — the paid apps ARE the purchase — and not the demo or the clip review (`isClipMode`),
 * which have their own, shorter allowance and their own end card. `?locked` forces it and
 * `?unlocked` lifts it for review; devFlag() is inert on production, so neither can change it for a
 * real player.
 */
export function isWebPlayLocked(lessonKey: string): boolean {
  if (!isWeb() || isClipMode()) return false;
  if (devFlag('unlocked')) return false;
  return devFlag('locked') || lessonStarsReached(lessonKey) >= WEB_FREE_STARS;
}

/**
 * Locale of the store badge artwork to use. Apple and Google publish one badge image per
 * language and both forbid re-typesetting them, so we ship the official file per UI language
 * (public/images/badges/). `de-CH` reuses the German badge — neither store publishes a Swiss one.
 */
const BADGE_LANGS = ['en', 'de', 'fr', 'es', 'it', 'nl', 'ja', 'ko', 'th'] as const;

export function badgeLang(): string {
  const base = (i18next.language || 'en').split('-')[0];
  return (BADGE_LANGS as readonly string[]).includes(base) ? base : 'en';
}
